import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
    useDeleteAllUsersMutation,
    useDeleteJourneysAndSchedulesMutation,
    useDeleteTenancyMutation,
    useGetReminderOptionsQuery,
    useUpdateReminderOptionsMutation,
    useUpdateTenancyMutation,
    useRefreshReportsMutation,
    useGetTenancyBasicsQuery,
    useGetFeatureSwitchesQuery,
    useSetLineManagerReportingLevelMutation,
    useSetLineManagerOnDemandLevelMutation
}
    from "../../store/services/tenancy.service";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Snackbar,
    TextField,
    Typography,
    Tooltip,
    MenuItem,
    Select,
    FormControl,
} from "@mui/material";
import {
    useGetSchedulerSettingsQuery,
    useUpdateSchedulerSettingsMutation
} from "../../store/services/scheduler.service";

import {
    useGetExcelReportSettingsQuery,
    useSendExcelReportSettingsMutation,
    useSendGenerateExcelReportMutation
} from "../../store/services/report.service";

import HelpIcon from "@mui/icons-material/HelpOutline";
import MainCard from "../../components/cards/MainCard";
import LoadingSwitch from "../../components/LoadingSwitch";

import Divider from "@mui/material/Divider";
import { useTheme } from "@mui/material/styles";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
    useGetUniversityCourseSettingsQuery,
    useSaveUniversityCourseSettingsMutation
} from "../../store/services/university-service";
import ConfirmationDialog from "../../utils/confirmationDialog";
import { useSendTenancyReminderEmailMutation } from "../../store/services/response.service";
import { getCardColor } from "../../utils/colorUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import CustomEmailDomainSettings from "./components/CustomEmailDomainSettings";
import MfaSettings from "./components/MfaSettings";

TenancySettings.propTypes = {
    data: PropTypes.object
};

export default function TenancySettings(props) {
    const { data } = props;
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";
    const environment = process.env.REACT_APP_BRAND_TYPE === 'censornet';
    const environmentName = process.env.REACT_APP_ENVIRONMENT_NAME;
    const navigate = useNavigate();
    const [tenancyData, setTenancyData] = useState(data);
    const [isAdmin, setIsAdmin] = useState(false);

    // Settings state variables
    const [validHeaderKey, setValidHeaderKey] = useState(1);
    const [validHeaderValue, setValidHeaderValue] = useState(1);
    const [reminderOptions, setReminderOptions] = useState({
        alternateUserId: null,
        dateCreated: "1970-01-01T00:00:00.000000",
        dateUpdated: "1970-01-01T00:00:00.000000",
        dateSent: "1970-01-01T00:00:00.000000",
        id: 0,
        userId: "",
        uniqueIdentifier: "",
        tenancyId: tenancyData.tenancyId,
        daysToRemindAfter: 0,
        reminderFrequency: 0
    });
    const [updatingSchedulerSettings, setUpdatingSchedulerSettings] = useState(false);
    const [emailHeaderKeyError, setEmailHeaderKeyError] = useState("");
    const [emailHeaderValueError, setEmailHeaderValueError] = useState("");

    //Button loading states
    const [isDeletingJourney, setIsDeletingJourney] = useState(false);
    const [isDeletingUsers, setIsDeletingUsers] = useState(false);
    const [isDeletingTenancy, setIsDeletingTenancy] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isSendingReminders, setIsSendingReminders] = useState(false);
    const [isSavingReminders, setIsSavingReminders] = useState(false);
    const [isSettingReportingLevel, setIsSettingReportingLevel] = useState(false);
    const [isSettingOnDemandLevel, setIsSettingOnDemandLevel] = useState(false);

    // Switch loading states
    const [loadingStates, setLoadingStates] = useState({
        enableCourseRetake: false,
        allowUnauthorisedTakeCourse: false,
        automaticCourseAssignmentDays: false,
        enableReporting: false,
        anonymiseReports: false,
        enableOndemand: false,
        enableJourneyBuilder: false,
        isPartnerTenancy: false,
        isHierarchyTenancy: false,
        hideGLobalCourses: false,
        hideGlobalContentPacks: false,
        hideGlobalJourneyTemplates: false,
        hideGlobalSimulations: false,
        hideGlobalCollections: false,
        enableMfa: false,
        enableLineManagerEscalation: false,
        enableTeamsNotification: false,
        enableEmailNotification: false
    });


    // Confirmation Dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogContent, setDialogContent] = useState("");
    const [dialogAction, setDialogAction] = useState(null);
    const [dialogCancelAction, setDialogCancelAction] = useState(null);

    // Snackbar
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    // Mutations
    const [updateTenancy] = useUpdateTenancyMutation();
    const [updateUniversityCourseSettings] = useSaveUniversityCourseSettingsMutation();
    const [deleteAllUsers] = useDeleteAllUsersMutation();
    const [deleteTenancy] = useDeleteTenancyMutation();
    const [deleteJourneysSchedules] = useDeleteJourneysAndSchedulesMutation();
    const [updateSchedulerSettings] = useUpdateSchedulerSettingsMutation();
    const [sendTenancyReminderEmail] = useSendTenancyReminderEmailMutation();
    const [updateReminderOptions] = useUpdateReminderOptionsMutation();
    const [refreshReports] = useRefreshReportsMutation();
    const [setLineManagerReportingLevel] = useSetLineManagerReportingLevelMutation();
    const [setLineManagerOnDemandLevel] = useSetLineManagerOnDemandLevelMutation();

    // Emails
    const [currentEmail, setCurrentEmail] = useState('');
    const [emails, setEmails] = useState([]);

    const {
        data: excelReportSettings,
        error: excelReportError,
        isLoading: excelReportLoading,
        refetch: refetchExcelSettings
    } = useGetExcelReportSettingsQuery({ tenancyId: tenancyData.tenancyId });
    const [sendExcelSettings] = useSendExcelReportSettingsMutation();
    const [isSavingExcelSettings, setIsSavingExcelSettings] = useState(false);
    const [sendGenerateReport, { isLoading: isGeneratingReport }] = useSendGenerateExcelReportMutation();

    // Get tenancy ID and feature switches
    const { tenancyId } = useSelector((state) => state.tenancy);
    const { data: featureSwitchesApiData } = useGetFeatureSwitchesQuery({ tenancyId });
    const featureSwitches = featureSwitchesApiData?.data || featureSwitchesApiData;

    // Queries 
    const {
        data: schedulerSettings,
        error: schedulerSettingsError,
        isLoading: schedulerSettingsLoading
    } = useGetSchedulerSettingsQuery({ tenancyId: tenancyData.tenancyId });

    const {
        data: tenancyReminderData,
        isLoading: tenancyReminderLoading,
        error: tenancyReminderError
    } = useGetReminderOptionsQuery({ tenancyId: tenancyData.tenancyId });

    const {
        data: universitySettings,
        error: universitySettingsError,
        isLoading: universitySettingsLoading,
        refetch: refetchUniversitySettings
    } = useGetUniversityCourseSettingsQuery({ tenancyId: tenancyData.tenancyId });

    const {
        data: tenancyBasicData,
        isLoading: tenancyBasicDataLoading,
        isError: tenancyBasicDataError
    } = useGetTenancyBasicsQuery({ tenancyId: tenancyData.tenancyId }, { skip: !tenancyData.tenancyId });


    const [unauthSendingBox, setUnauthSendingBox] = useState(universitySettings ? universitySettings?.allowUnauthorisedTakeCourse : false);

    useEffect(() => {
        if (tenancyReminderData)
            setReminderOptions(tenancyReminderData);
    }, [tenancyReminderData]);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        setIsAdmin(user && user.role === "Administrators");
    }, []);

    useEffect(() => {
        if (universitySettings) {
            setTenancyData(prev => ({
                ...prev,
                allowUnauthorisedTakeCourse: universitySettings.allowUnauthorisedTakeCourse
            }));
        }
    }, [universitySettings]);

    useEffect(() => {
        if (excelReportSettings?.emails) {
            setEmails(excelReportSettings.emails);
        }
    }, [excelReportSettings]);

    const handleSaveExcelSettings = async () => {
        setIsSavingExcelSettings(true);
        try {
            await sendExcelSettings({
                tenancyId: tenancyData.tenancyId,
                data: {
                    tenancyId: tenancyData.tenancyId,
                    emails: emails
                }
            }).unwrap();

            setSnackbarMessage("Excel report settings saved successfully");
            setSnackbarSeverity("success");
            await refetchExcelSettings();
        } catch (error) {
            setSnackbarMessage("Error saving Excel report settings");
            setSnackbarSeverity("error");
        } finally {
            setSnackbarOpen(true);
            setIsSavingExcelSettings(false);
        }
    };

    const handleSave = async (updatedData) => {
        if (validHeaderValue !== 1 || validHeaderKey !== 1) {
            setSnackbarMessage("Invalid email header key or value");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return Promise.reject();
        } else if (updatedData.userLicenses < 0 || updatedData.userLicenses < updatedData.adminCount + updatedData.userCount) {
            setSnackbarMessage("Invalid number of user licenses");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return Promise.reject();
        } else {
            try {

                await updateTenancy({
                    data: updatedData,
                    tenancyId: updatedData.tenancyId
                }).unwrap();

                setSnackbarMessage("Settings updated successfully.");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
            } catch (error) {
                console.log("Error updating tenancy settings:", error);
                setSnackbarMessage("Error updating tenancy settings.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                throw error;
            }
        }
    };


    const setLoadingFor = (setting, isLoading) => {
        setLoadingStates(prev => ({
            ...prev,
            [setting]: isLoading
        }));
    };

    const isAnySwitchLoading = (loadingStates) => {
        return Object.values(loadingStates).some(state => state === true);
    };

    // statement is to handle any legacy data and change it to either "-1" or "0" where -1 = false and 0 = true
    const isAutoCourseAssignmentEnabled = (value) => value !== "-1" && value !== null;

    const handleSwitchChange = async (field) => {
        setLoadingFor(field, true);

        const newValue = field === 'automaticCourseAssignmentDays'
            ? (isAutoCourseAssignmentEnabled(tenancyData[field]) ? "-1" : "0")
            : !tenancyData[field];

        // If anonymiseReports is selected, check that the tenancy does not have a parent that is anything other than "0" or "-1"
        if (field === 'anonymiseReports' && tenancyData.parentTenancyId !== 0 && tenancyData.parentTenancyId !== -1) {
            setSnackbarMessage("Anonymise reports cannot be enabled on child tenancies.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            setLoadingFor(field, false);
            return;
        }

        const updatedData = {
            ...tenancyData,
            [field]: newValue
        };

        try {
            await updateTenancy({
                data: updatedData,
                tenancyId: updatedData.tenancyId
            }).unwrap();

            // Only update state after successful API call
            setTenancyData(updatedData);
            setSnackbarMessage("Settings updated successfully.");
            setSnackbarSeverity("success");
        } catch (error) {
            console.log("Error updating tenancy settings:", error);
            setSnackbarMessage("Error updating tenancy settings.");
            setSnackbarSeverity("error");
        } finally {
            setSnackbarOpen(true);
            setLoadingFor(field, false);
        }
    };

    const handleSendReminders = async () => {
        setDialogTitle("Send Reminders");
        setDialogContent("Are you sure you want to send reminders?");
        setDialogAction(() => async () => {
            setIsSendingReminders(true);
            await sendTenancyReminderEmail({ tenancyId: tenancyData.tenancyId }).unwrap().then(() => {
                setSnackbarMessage("Reminders sent successfully.");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
            }).catch(() => {
                setSnackbarMessage("Error sending reminders.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            });
            setIsSendingReminders(false);
        });
        setDialogOpen(true);
    };


    const handleSaveReminderOptions = () => {
        setIsSavingReminders(true);
        updateReminderOptions({ data: reminderOptions })
            .unwrap()
            .then(() => {
                setSnackbarMessage("Reminder options saved successfully");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
            })
            .catch(() => {
                setSnackbarMessage("Error updating reminder options");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            })
            .finally(() => {
                setIsSavingReminders(false);
            });
    };

    const handleRefreshReports = async () => {
        setIsRefreshing(true);
        setDialogTitle("Refresh Reports");
        setDialogContent("Are you sure you want to refresh the reports? This may take a few moments.");
        setDialogCancelAction(() => () => setIsRefreshing(false));
        setDialogAction(() => async () => {
            try {
                await refreshReports(tenancyData.tenancyId).unwrap();
                setSnackbarMessage("Reports refreshed successfully.");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
            } catch (error) {
                setSnackbarMessage(`Error refreshing reports: ${error.message || 'Unknown error'}`);
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            } finally {
                setIsRefreshing(false);
            }
        });
        setDialogOpen(true);
    };


    const handleSaveUniversityCourseSettings = () => {
        updateUniversityCourseSettings({
            tenancyId: tenancyData.tenancyId,
            settingsData: { tenancyId: tenancyData.tenancyId, allowUnauthorisedTakeCourse: unauthSendingBox }
        })
            .then(() => {
                setSnackbarMessage("Tenancy settings updated successfully.");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
            })
            .catch((error) => {
                console.log("Error updating Tenancy settings:", error);
                setSnackbarMessage("Error updating Tenancy settings.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            });
    };

    const handleEmailHeaderChange = (type, value) => {
        switch (type) {
            case "key":
                setTenancyData({ ...tenancyData, customEmailHeaderKey: value });
                // Check for X-BPSAT
                if (value.toUpperCase() === "X-BPSAT") {
                    setValidHeaderKey(-1);
                    setEmailHeaderKeyError("Default email header key cannot be used");
                } else if (/^[a-zA-Z0-9-_]+$/.test(value)) {
                    setValidHeaderKey(1);
                    setEmailHeaderKeyError("");
                } else {
                    setValidHeaderKey(-1);
                    setEmailHeaderKeyError("Key must only contain letters, numbers, hyphens, and underscores");
                }
                return;

            case "value":
                setTenancyData({ ...tenancyData, customEmailHeaderValue: value });
                if (value.toUpperCase() === "66Y7U72LTS") {
                    setValidHeaderValue(-1);
                    setEmailHeaderValueError("Default email header value cannot be used");
                } else if (/^[a-zA-Z0-9 _:;.,\/"'?!(){}[\]@<>=\-+*#$&`|~^%]+$/.test(value)) {
                    setValidHeaderValue(1);
                    setEmailHeaderValueError("");
                } else {
                    setValidHeaderValue(-1);
                    setEmailHeaderValueError("Value contains invalid characters");
                }
                return;
        }
    };

    const handleDeleteJourneysSchedules = () => {
        setIsDeletingJourney(true)
        setDialogTitle("Delete Active Journey");
        setDialogContent("Are you sure you want to delete the active journey? This action is irreversible.");
        setDialogCancelAction(() => () => setIsDeletingJourney(false));
        setDialogAction(() => () => {
            deleteJourneysSchedules({ tenancyId: tenancyData.tenancyId }).unwrap().then((response) => {
                setSnackbarMessage("Journey deleted successfully");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
                setIsDeletingJourney(false)
            }).catch((error) => {
                setSnackbarMessage("Error deleting journey");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                console.log("Error:", error);
                setIsDeletingJourney(false)
            });
        });
        setDialogOpen(true);
    };

    const handleDeleteUsers = () => {
        setIsDeletingUsers(true);
        setDialogTitle("Delete All Users");
        setDialogContent("Are you sure you want to delete all users? This action is irreversible.");
        setDialogCancelAction(() => () => setIsDeletingUsers(false));
        setDialogAction(() => () => {
            deleteAllUsers({ tenancyId: tenancyData.tenancyId }).unwrap().then((response) => {
                setSnackbarMessage("Users deleted successfully");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
                setIsDeletingUsers(false);
            }).catch((error) => {
                setSnackbarMessage("Error deleting users request");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                console.log("Error:", error);
                setIsDeletingUsers(false);
            });
        });
        setDialogOpen(true);
    };

    const handleDeleteTenancy = () => {
        setIsDeletingTenancy(true)
        setDialogTitle("Delete Tenancy");
        setDialogContent("Are you sure you want to delete this tenancy? This action is irreversible.");
        setDialogCancelAction(() => () => setIsDeletingTenancy(false));
        setDialogAction(() => () => {
            deleteTenancy({ tenancyId: tenancyData.tenancyId }).unwrap().then((response) => {
                setSnackbarMessage("Tenancy deleted successfully.");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
                setIsDeletingTenancy(false);
                navigate(-1);
            }).catch((error) => {
                setSnackbarMessage("Error deleting tenancy.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                console.log("Error deleting tenancy:", error);
                setIsDeletingTenancy(false);
            });
        });
        setDialogOpen(true);
    };

    const handleOneClickCoursesToggle = async () => {
        setLoadingFor('allowUnauthorisedTakeCourse', true);
        try {
            const newValue = !tenancyData.allowUnauthorisedTakeCourse;

            // Create update data with all existing settings
            const updateData = {
                ...universitySettings, // Preserve all existing university settings
                tenancyId: tenancyData.tenancyId,
                allowUnauthorisedTakeCourse: newValue
            };

            // Update university settings
            await updateUniversityCourseSettings({
                tenancyId: tenancyData.tenancyId,
                settingsData: updateData
            }).unwrap();

            // Refetch to ensure we have the latest data
            await refetchUniversitySettings();

            // Update local state while preserving all other fields
            setTenancyData(prev => ({
                ...prev,
                allowUnauthorisedTakeCourse: newValue
            }));

            setSnackbarMessage("One-click courses setting updated successfully.");
            setSnackbarSeverity("success");
        } catch (error) {
            console.error("Error updating one-click courses setting:", error);
            setSnackbarMessage("Error updating one-click courses setting.");
            setSnackbarSeverity("error");
        } finally {
            setSnackbarOpen(true);
            setLoadingFor('allowUnauthorisedTakeCourse', false);
        }
    };

    const handleUpdateSchedulerSettings = async () => {
        const data = {
            pauseSending: !schedulerSettings.pauseSending,
            tenancyId: schedulerSettings.tenancyId,
            currentJourney: schedulerSettings.currentJourney
        };
        setUpdatingSchedulerSettings(true);
        setDialogTitle((data.pauseSending ? "Pause" : "Resume") + " Journeys?");
        setDialogContent("Are you sure you want to " + (data.pauseSending ? "pause" : "resume") + " journeys?");
        setDialogCancelAction(() => () => setUpdatingSchedulerSettings(false));
        setDialogAction(() => async () => {
            try {
                const response = await updateSchedulerSettings({
                    tenancyId: tenancyData.tenancyId,
                    data: data
                }).unwrap();
                setSnackbarMessage((data.pauseSending ? "paused" : "resumed") + " journeys successfully.");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
            } catch (error) {
                setSnackbarMessage("Error " + (data.pauseSending ? "pausing" : "resuming") + " journeys");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                console.log("Error updating journey settings:", error);
            } finally {
                setUpdatingSchedulerSettings(false);
            }
        });
        setDialogOpen(true);
    };

    const handleSaveUserLicenses = () => {
        handleSave(tenancyData);
    };

    const handleAddEmail = () => {
        if (currentEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentEmail)) {
            setEmails([...emails, currentEmail]);
            setCurrentEmail('');
        } else {
            setSnackbarMessage("Please enter a valid email address");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    const handleRemoveEmail = (emailToRemove) => {
        setEmails(emails.filter(email => email !== emailToRemove));
    };

    const handleGenerateReport = async () => {
        try {
            await sendGenerateReport({
                tenancyId: tenancyData.tenancyId
            }).unwrap();
            setSnackbarMessage("Excel report generation initiated successfully");
            setSnackbarSeverity("success");
        } catch (error) {
            setSnackbarMessage("Error generating Excel report");
            setSnackbarSeverity("error");
        } finally {
            setSnackbarOpen(true);
        }
    };

    const handleSetLineManagerReportingLevel = () => {
        setDialogTitle("Set Line Manager Reporting Level");
        setDialogContent("Are you sure you want to set the Reporting Level to 'Line Manager' for all line managers who currently have it as null?");
        setDialogAction(() => async () => {
            setIsSettingReportingLevel(true);
            try {
                const response = await setLineManagerReportingLevel({ tenancyId: tenancyData.tenancyId }).unwrap();
                const updatedCount = response.updatedCount || 0;
                setSnackbarMessage(`Updated ${updatedCount} line managers with reporting level 'Line Manager'`);
                setSnackbarSeverity("success");
            } catch (error) {
                setSnackbarMessage("Error setting line manager reporting levels");
                setSnackbarSeverity("error");
            } finally {
                setIsSettingReportingLevel(false);
                setSnackbarOpen(true);
            }
        });
        setDialogOpen(true);
    };

    const handleSetLineManagerOnDemandLevel = () => {
        setDialogTitle("Set Line Manager On-Demand Level");
        setDialogContent("Are you sure you want to set the On-Demand Level to 'Line Manager' for all line managers who currently have it as null?");
        setDialogAction(() => async () => {
            setIsSettingOnDemandLevel(true);
            try {
                const response = await setLineManagerOnDemandLevel({ tenancyId: tenancyData.tenancyId }).unwrap();
                const updatedCount = response.updatedCount || 0;
                setSnackbarMessage(`Updated ${updatedCount} line managers with on-demand level 'Line Manager'`);
                setSnackbarSeverity("success");
            } catch (error) {
                setSnackbarMessage("Error setting line manager on-demand levels");
                setSnackbarSeverity("error");
            } finally {
                setIsSettingOnDemandLevel(false);
                setSnackbarOpen(true);
            }
        });
        setDialogOpen(true);
    };

    // Render component
    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                    }}>
                        <Typography variant="h2" mr={4}>{tenancyData.name}</Typography>
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2
                        }}>
                            {featureSwitches?.enablePauseJourneys && (
                                <Button
                                    size="medium"
                                    variant="contained"
                                    color={schedulerSettings?.pauseSending ? "error" : "primary"}
                                    onClick={handleUpdateSchedulerSettings}
                                    disabled={schedulerSettingsLoading || schedulerSettingsError || !schedulerSettings}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {updatingSchedulerSettings ? <CircularProgress size={24} color="inherit" /> : (schedulerSettings?.pauseSending ? "Emails Paused" : "Pause Emails")}
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Divider sx={{ width: "100%" }} />
                </Grid>

                {/* Security Section */}
                <Grid item xs={12}>
                    {(
                        <Typography variant="h4" sx={{ ml: 1, mb: 2 }}>Security</Typography>
                    )}
                    <Grid container spacing={2}>
                        {/* Multi-Factor Authentication */}
                        {(
                            <Grid item xs={12} md={6} lg={4}>
                                <MfaSettings
                                    tenancyData={tenancyData}
                                    loadingStates={loadingStates}
                                    isAnySwitchLoading={isAnySwitchLoading}
                                    onSwitchChange={handleSwitchChange}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                {/* Courses Section */}
                <Grid item xs={12}>
                    {isAdmin && (
                        <Typography variant="h4" sx={{ ml: 1, mb: 2 }}>Courses</Typography>)}
                    <Grid container spacing={2}>
                        {/* Course retaking */}
                        {isAdmin && (
                            <Grid item xs={12} md={6} lg={4}>
                                <MainCard content={true} boxShadow={true} border={false}
                                    sx={{
                                        height: "100%",
                                        bgcolor: getCardColor(lightMode, tenancyData.enableCourseRetake) //courseRetakeBox ? (lightMode ? "primary.white" : "grey.600") : (lightMode ? "grey.200" : "#3c3e40")
                                    }}>
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: "100%",
                                        justifyContent: "space-between"
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Tooltip title="Allow users to retake a course if they scored below 80%. Enabling this will apply the settings to all existing courses, including those already assigned">
                                                <IconButton>
                                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Typography variant="h5">
                                                Course Retaking
                                            </Typography>
                                        </Box>
                                        <LoadingSwitch
                                            checked={tenancyData.enableCourseRetake}
                                            onChange={() => handleSwitchChange('enableCourseRetake')}
                                            loading={loadingStates.enableCourseRetake}
                                            disabled={isAnySwitchLoading(loadingStates)}
                                            inputProps={{ "aria-label": "controlled" }}
                                        />
                                    </Box>
                                </MainCard>
                            </Grid>)}

                        {/* Course by email NOT IMPLEMENTED YET (REMOVE "false &&" when implemented) */}

                        {isAdmin && (
                            <Grid item xs={12} md={6} lg={4}>
                                {universitySettingsLoading ? (
                                    <MainCard content={true} boxShadow={true} border={true}
                                        sx={{
                                            height: "100%",
                                            bgcolor: getCardColor(lightMode),
                                            alignContent: "center"
                                        }}>
                                        <Box sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            width: "100%",
                                            height: "100%"
                                        }}>
                                            <CircularProgress size={32} sx={{ mr: 2 }} />
                                            <Typography variant="h6">
                                                Loading One-Click Courses Setting
                                            </Typography>
                                        </Box>
                                    </MainCard>
                                ) : universitySettingsError ? (
                                    <MainCard content={true} boxShadow={true} border={true}
                                        sx={{
                                            height: "100%",
                                            bgcolor: getCardColor(lightMode),
                                            alignContent: "center"
                                        }}>
                                        <Box sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            width: "100%",
                                            height: "100%"
                                        }}>
                                            <ErrorOutlineIcon color="error" style={{ fontSize: 32, marginRight: 8 }} />
                                            <Typography variant="h6" color="error">
                                                Error Loading One-Click Courses Setting
                                            </Typography>
                                        </Box>
                                    </MainCard>
                                ) : (
                                    <MainCard content={true} boxShadow={true} border={false}
                                        sx={{
                                            height: "100%",
                                            bgcolor: getCardColor(lightMode, tenancyData.allowUnauthorisedTakeCourse)
                                        }}>
                                        <Box sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            width: "100%",
                                            justifyContent: "space-between"
                                        }}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <Tooltip title="When enabled, course invitation emails will include a link to take the course directly through email, circumventing the need for the user to login">
                                                    <IconButton>
                                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Typography variant="h5">
                                                    One-Click Courses
                                                </Typography>
                                            </Box>
                                            <LoadingSwitch
                                                checked={tenancyData.allowUnauthorisedTakeCourse}
                                                onChange={handleOneClickCoursesToggle}
                                                inputProps={{ "aria-label": "controlled" }}
                                                disabled={isAnySwitchLoading(loadingStates)}
                                                loading={loadingStates.allowUnauthorisedTakeCourse}
                                            />
                                        </Box>
                                    </MainCard>
                                )}
                            </Grid>
                        )}

                        {/* Automatic course assignment */}
                        {isAdmin && (
                            <Grid item xs={12} md={6} lg={4}>
                                <MainCard content={true} boxShadow={true} border={false}
                                    sx={{ height: "100%", bgcolor: getCardColor(lightMode, isAutoCourseAssignmentEnabled(tenancyData.automaticCourseAssignmentDays)) }}>
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: "100%",
                                        justifyContent: "space-between"
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Tooltip title="Users are automatically assigned a course following the click of a link within a phishing simulation">
                                                <IconButton>
                                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Typography variant="h5">
                                                Automatic Course Assignment
                                            </Typography>
                                        </Box>
                                        <LoadingSwitch
                                            checked={isAutoCourseAssignmentEnabled(tenancyData.automaticCourseAssignmentDays)}
                                            onChange={() => handleSwitchChange('automaticCourseAssignmentDays')}
                                            loading={loadingStates.automaticCourseAssignmentDays}
                                            disabled={isAnySwitchLoading(loadingStates)}
                                            inputProps={{ "aria-label": "controlled" }}
                                        />
                                    </Box>
                                </MainCard>
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    {isAdmin && (
                        <Typography variant="h4" sx={{ ml: 1, mb: 2 }}>Content Access</Typography>)}
                    <Grid container spacing={2}>
                        {/* Global course access */}
                        {isAdmin && (
                            <Grid item xs={12} md={6} lg={4}>
                                <MainCard content={true} boxShadow={true} border={false}
                                    sx={{ height: "100%", bgcolor: getCardColor(lightMode, tenancyData.hideGlobalCourses) }}>
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: "100%",
                                        justifyContent: "space-between"
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Tooltip title="Courses that are assigned to all tenancies will be hidden from this tenancy">
                                                <IconButton>
                                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Typography variant="h5">
                                                Hide Global Courses
                                            </Typography>
                                        </Box>
                                        <LoadingSwitch
                                            checked={tenancyData.hideGlobalCourses}
                                            onChange={() => handleSwitchChange('hideGlobalCourses')}
                                            inputProps={{ "aria-label": "controlled" }}
                                            disabled={isAnySwitchLoading(loadingStates)}
                                            loading={loadingStates.hideGlobalCourses}
                                        />
                                    </Box>
                                </MainCard>
                            </Grid>
                        )}

                        {/* Global content pack access */}
                        {isAdmin && (
                            <Grid item xs={12} md={6} lg={4}>
                                <MainCard content={true} boxShadow={true} border={false}
                                    sx={{ height: "100%", bgcolor: getCardColor(lightMode, tenancyData.hideGlobalContentPacks) }}>
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: "100%",
                                        justifyContent: "space-between"
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Tooltip title="Courses that are assigned to all tenancies will be hidden from this tenancy">
                                                <IconButton>
                                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Typography variant="h5">
                                                Hide Global Content Packs
                                            </Typography>
                                        </Box>
                                        <LoadingSwitch
                                            checked={tenancyData.hideGlobalContentPacks}
                                            onChange={() => handleSwitchChange('hideGlobalContentPacks')}
                                            inputProps={{ "aria-label": "controlled" }}
                                            disabled={isAnySwitchLoading(loadingStates)}
                                            loading={loadingStates.hideGlobalContentPacks}
                                        />
                                    </Box>
                                </MainCard>
                            </Grid>
                        )}

                        {/* Global journey template access */}
                        {isAdmin && (
                            <Grid item xs={12} md={6} lg={4}>
                                <MainCard content={true} boxShadow={true} border={false}
                                    sx={{ height: "100%", bgcolor: getCardColor(lightMode, tenancyData.hideGlobalJourneyTemplates) }}>
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: "100%",
                                        justifyContent: "space-between"
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Tooltip title="Templates that are assigned to all tenancies will be hidden from this tenancy">
                                                <IconButton>
                                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Typography variant="h5">
                                                Hide Global Journey Templates
                                            </Typography>
                                        </Box>
                                        <LoadingSwitch
                                            checked={tenancyData.hideGlobalJourneyTemplates}
                                            onChange={() => handleSwitchChange('hideGlobalJourneyTemplates')}
                                            inputProps={{ "aria-label": "controlled" }}
                                            disabled={isAnySwitchLoading(loadingStates)}
                                            loading={loadingStates.hideGlobalJourneyTemplates}
                                        />
                                    </Box>
                                </MainCard>
                            </Grid>
                        )}

                        {/* Global simulation access */}
                        {isAdmin && (
                            <Grid item xs={12} md={6} lg={4}>
                                <MainCard content={true} boxShadow={true} border={false}
                                    sx={{ height: "100%", bgcolor: getCardColor(lightMode, tenancyData.hideGlobalSimulations) }}>
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: "100%",
                                        justifyContent: "space-between"
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Tooltip title="Templates that are assigned to all tenancies will be hidden from this tenancy">
                                                <IconButton>
                                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Typography variant="h5">
                                                Hide Global Simulations
                                            </Typography>
                                        </Box>
                                        <LoadingSwitch
                                            checked={tenancyData.hideGlobalSimulations}
                                            onChange={() => handleSwitchChange('hideGlobalSimulations')}
                                            inputProps={{ "aria-label": "controlled" }}
                                            disabled={isAnySwitchLoading(loadingStates)}
                                            loading={loadingStates.hideGlobalSimulations}
                                        />
                                    </Box>
                                </MainCard>
                            </Grid>
                        )}


                        {/* Global simulation collection access */}
                        {isAdmin && (
                            <Grid item xs={12} md={6} lg={4}>
                                <MainCard content={true} boxShadow={true} border={false}
                                    sx={{ height: "100%", bgcolor: getCardColor(lightMode, tenancyData.hideGlobalCollections) }}>
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: "100%",
                                        justifyContent: "space-between"
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Tooltip title="Simulation collections that are assigned to all tenancies will be hidden from this tenancy">
                                                <IconButton>
                                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Typography variant="h5">
                                                Hide Global Collections
                                            </Typography>
                                        </Box>
                                        <LoadingSwitch
                                            checked={tenancyData.hideGlobalCollections}
                                            onChange={() => handleSwitchChange('hideGlobalCollections')}
                                            inputProps={{ "aria-label": "controlled" }}
                                            disabled={isAnySwitchLoading(loadingStates)}
                                            loading={loadingStates.hideGlobalCollections}
                                        />
                                    </Box>
                                </MainCard>
                            </Grid>
                        )}
                    </Grid>

                    {/* Reports Section - Only Refresh Reports Button */}
                    {isAdmin && (
                        <>
                            <Typography variant="h4" sx={{ ml: 1, mt: 2, mb: 2 }}>Reports</Typography>
                            <Grid container spacing={2}>
                                {/* Refresh reports */}
                                <Grid item xs={12} md={6} lg={4}>
                                    <MainCard content={true} boxShadow={true} border={false}
                                        sx={{ height: "100%", bgcolor: getCardColor(lightMode) }}>
                                        <Box sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            width: "100%",
                                            justifyContent: "space-between"
                                        }}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <Tooltip title="Manually refresh Reports access following recent changes">
                                                    <IconButton>
                                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Typography variant="h5">
                                                    Refresh Reports
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={handleRefreshReports}
                                                disabled={isRefreshing}
                                            >
                                                {isRefreshing ? <CircularProgress size={24} color="inherit" /> : "REFRESH"}
                                            </Button>
                                        </Box>
                                    </MainCard>
                                </Grid>
                                {/* Anonymize reports */}
                                <Grid item xs={12} md={6} lg={4}>
                                    <MainCard content={true} boxShadow={true} border={false}
                                        sx={{
                                            height: "100%",
                                            bgcolor: getCardColor(lightMode, tenancyData.anonymiseReports) //anonBox ? (lightMode ? "primary.white" : "grey.600") : (lightMode ? "grey.200" : "grey.700")
                                        }}>
                                        <Box sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            width: "100%",
                                            justifyContent: "space-between"
                                        }}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <Tooltip title=" Anonymise all data within Reports. Effective immediately">
                                                    <IconButton>
                                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Typography variant="h5">
                                                    Anonymize Reports
                                                </Typography>
                                            </Box>
                                            <LoadingSwitch
                                                checked={tenancyData.anonymiseReports}
                                                onChange={() => handleSwitchChange('anonymiseReports')}
                                                inputProps={{ "aria-label": "controlled" }}
                                                disabled={isAnySwitchLoading(loadingStates)}
                                                loading={loadingStates.anonymiseReports}
                                            />
                                        </Box>
                                    </MainCard>
                                </Grid>
                            </Grid>
                        </>
                    )}

                    {/* Notifications Section */}
                    {featureSwitches?.enableNotifications && featureSwitches?.enableTeamsIntegration && (
                        <>
                            <Typography variant="h4" sx={{ ml: 1, mt: 2, mb: 2 }}>Notifications</Typography>
                            <Grid container spacing={2}>
                                {/* Teams Notifications */}
                                <Grid item xs={12} md={6} lg={4}>
                                    <MainCard content={true} boxShadow={true} border={false}
                                        sx={{ height: "100%", bgcolor: getCardColor(lightMode, tenancyData.enableTeamsNotification) }}>
                                        <Box sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            width: "100%",
                                            justifyContent: "space-between"
                                        }}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <Tooltip title="Enable Microsoft Teams notifications for your users">
                                                    <IconButton>
                                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Typography variant="h5">
                                                    Teams
                                                </Typography>
                                            </Box>
                                            <LoadingSwitch
                                                checked={tenancyData.enableTeamsNotification}
                                                onChange={() => handleSwitchChange('enableTeamsNotification')}
                                                loading={loadingStates.enableTeamsNotification}
                                                disabled={isAnySwitchLoading(loadingStates)}
                                                inputProps={{ "aria-label": "controlled" }}
                                            />
                                        </Box>
                                    </MainCard>
                                </Grid>

                                {/* Email Notifications */}
                                <Grid item xs={12} md={6} lg={4}>
                                    <MainCard content={true} boxShadow={true} border={false}
                                        sx={{ height: "100%", bgcolor: getCardColor(lightMode, tenancyData.enableEmailNotification) }}>
                                        <Box sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            width: "100%",
                                            justifyContent: "space-between"
                                        }}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <Tooltip title="Enable email notifications for your users">
                                                    <IconButton>
                                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Typography variant="h5">
                                                    Emails
                                                </Typography>
                                            </Box>
                                            <LoadingSwitch
                                                checked={tenancyData.enableEmailNotification}
                                                onChange={() => handleSwitchChange('enableEmailNotification')}
                                                loading={loadingStates.enableEmailNotification}
                                                disabled={isAnySwitchLoading(loadingStates)}
                                                inputProps={{ "aria-label": "controlled" }}
                                            />
                                        </Box>
                                    </MainCard>
                                </Grid>
                            </Grid>
                        </>
                    )}

                    <Grid container spacing={2} sx={{ mb: 6 }}>
                        {/* Custom Email Header */}
                        {featureSwitches?.enableCustomEmailHeaders && (
                            <Grid item xs={12} sm={12} lg={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2 }}>
                                    <Typography variant="h4" sx={{ ml: 1 }}>
                                        Custom Email Header
                                    </Typography>
                                    <Tooltip title="Optional email headers to be included in each email sent from the portal">
                                        <IconButton>
                                            <HelpIcon sx={{ color: 'secondary.main' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <MainCard content={true} boxShadow={true} border={false}
                                    sx={{ height: "87%", bgcolor: getCardColor(lightMode) }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <Tooltip title="Key must only contain letters (a-z, A-Z), numbers (0-9), hyphens (-), and underscores (_).">
                                                    <IconButton size="small">
                                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Typography variant="h5" sx={{ ml: 1 }}>
                                                    Email Header Key
                                                </Typography>
                                            </Box>
                                            <TextField
                                                placeholder="INSERT HERE"
                                                size="small"
                                                sx={{ width: "50%" }}
                                                onChange={(e) => handleEmailHeaderChange("key", e.target.value)}
                                                value={tenancyData.customEmailHeaderKey}
                                                error={validHeaderKey === -1}
                                                helperText={emailHeaderKeyError}
                                            />
                                        </Box>
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <Tooltip title={"Value must only contain letters, numbers, and special characters (_ :;.,\\/\"'?!()[]@<>=-+*#$&`|~^%)."}>
                                                    <IconButton size="small">
                                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Typography variant="h5" sx={{ ml: 1 }}>
                                                    Email Header Value
                                                </Typography>
                                            </Box>
                                            <TextField
                                                placeholder="INSERT HERE"
                                                size="small"
                                                sx={{ width: "50%" }}
                                                onChange={(e) => handleEmailHeaderChange("value", e.target.value)}
                                                value={tenancyData.customEmailHeaderValue}
                                                error={validHeaderValue === -1}
                                                helperText={emailHeaderValueError}
                                            />
                                        </Box>
                                        <Box sx={{ display: "flex", justifyContent: "end", mt: 2 }}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={() => handleSave(tenancyData)}
                                                sx={{ px: 3, whiteSpace: 'nowrap', maxWidth: '200px' }}
                                            >
                                                SAVE EMAIL HEADERS
                                            </Button>
                                        </Box>
                                    </Box>
                                </MainCard>
                            </Grid>
                        )}

                        {/* Custom Email Domain */}
                        {featureSwitches?.enableCustomEmailSubdomain && (
                            <Grid item xs={12} sm={12} lg={6}>
                                <CustomEmailDomainSettings
                                    tenancyId={String(tenancyData.tenancyId)}
                                />
                            </Grid>
                        )}

                        {/* Reminders */}
                        {featureSwitches?.enableReminders && (
                            <Grid item xs={12} sm={12} lg={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2 }}>
                                    <Typography variant="h4" sx={{ ml: 1 }}>
                                        Reminders
                                    </Typography>
                                    <Tooltip title="Set how often users will be emailed a reminder to complete any outstanding courses">
                                        <IconButton>
                                            <HelpIcon sx={{ color: 'secondary.main' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                {tenancyReminderLoading ? (
                                    <MainCard content={true} boxShadow={false} border={true}
                                        sx={{ height: "80%", bgcolor: "background.default", alignContent: "center" }}>
                                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                            <CircularProgress size={32} sx={{ mr: 2 }} />
                                            <Typography variant="h6">
                                                Loading Reminder Options
                                            </Typography>
                                        </Box>
                                    </MainCard>
                                ) : tenancyReminderError ? (
                                    <MainCard content={true} boxShadow={false} border={true}
                                        sx={{ height: "80%", bgcolor: "background.default", alignContent: "center" }}>
                                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                            <ErrorOutlineIcon color="error" style={{ fontSize: 32, marginRight: 8 }} />
                                            <Typography variant="h6" color="error">
                                                Error Loading Reminder Options
                                            </Typography>
                                        </Box>
                                    </MainCard>
                                ) : (
                                    <MainCard content={true} boxShadow={true} border={false}
                                        sx={{ height: "80%", bgcolor: getCardColor(lightMode) }}>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <Tooltip title="The number of days after the first assignment to send a reminder">
                                                        <IconButton size="small">
                                                            <HelpIcon sx={{ color: "secondary.main" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Typography variant="h5" sx={{ ml: 1 }}>
                                                        First Reminder
                                                    </Typography>
                                                </Box>
                                                <FormControl sx={{ width: "50%" }}>
                                                    <Select
                                                        variant="outlined"
                                                        value={reminderOptions.daysToRemindAfter.toString()}
                                                        onChange={(e) => {
                                                            setReminderOptions({
                                                                ...reminderOptions,
                                                                daysToRemindAfter: parseInt(e.target.value)
                                                            });
                                                        }}
                                                        size="small">
                                                        <MenuItem value="0">0</MenuItem>
                                                        <MenuItem value="7">7</MenuItem>
                                                        <MenuItem value="14">14</MenuItem>
                                                        <MenuItem value="21">21</MenuItem>
                                                        <MenuItem value="28">28</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <Tooltip title="The number of days reminders will reoccur after. e.g. 7 days after first reminder">
                                                        <IconButton size="small">
                                                            <HelpIcon sx={{ color: "secondary.main" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Typography variant="h5" sx={{ ml: 1 }}>
                                                        Following Reminders
                                                    </Typography>
                                                </Box>
                                                <FormControl sx={{ width: "50%" }}>
                                                    <Select
                                                        value={reminderOptions.reminderFrequency?.toString()}
                                                        onChange={(e) => {
                                                            setReminderOptions({
                                                                ...reminderOptions,
                                                                reminderFrequency: parseInt(e.target.value)
                                                            });
                                                        }}
                                                        size="small"
                                                        variant="outlined">
                                                        <MenuItem value="0">0</MenuItem>
                                                        <MenuItem value="7">7</MenuItem>
                                                        <MenuItem value="14">14</MenuItem>
                                                        <MenuItem value="21">21</MenuItem>
                                                        <MenuItem value="28">28</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>

                                            <Divider sx={{ width: "100%", mt: 2 }} />

                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <Tooltip title="Enable line manager escalation after a user recives two reminders in a row">
                                                        <IconButton size="small">
                                                            <HelpIcon sx={{ color: "secondary.main" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Typography variant="h5" sx={{ ml: 1 }}>
                                                        Line Manager Escalation
                                                    </Typography>
                                                </Box>
                                                <LoadingSwitch
                                                    checked={tenancyData.enableLineManagerEscalation}
                                                    onChange={() => handleSwitchChange('enableLineManagerEscalation')}
                                                    inputProps={{ "aria-label": "controlled" }}
                                                    disabled={isAnySwitchLoading(loadingStates)}
                                                    loading={loadingStates.enableLineManagerEscalation}
                                                />
                                            </Box>

                                            <Box sx={{ display: "flex", justifyContent: "end", mt: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleSendReminders}
                                                    disabled={isSendingReminders}
                                                    sx={{ px: 3, mr: 2, whiteSpace: 'nowrap', maxWidth: '200px' }}
                                                >
                                                    {isSendingReminders ? <CircularProgress size={24} color="inherit" /> : "SEND TO USERS"}
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={handleSaveReminderOptions}
                                                    sx={{ px: 3, whiteSpace: 'nowrap', maxWidth: '200px' }}
                                                >
                                                    {isSavingReminders ? <CircularProgress size={24} color="inherit" /> : "SAVE REMINDERS"}
                                                </Button>
                                            </Box>
                                        </Box>
                                    </MainCard>
                                )}
                            </Grid>
                        )}

                        {isAdmin && (
                            <Grid item xs={12} sm={12} lg={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2 }}>
                                    <Typography variant="h4" sx={{ ml: 1 }}>
                                        Excel Report
                                    </Typography>
                                    <Tooltip title="Set up email recipients for Excel reports">
                                        <IconButton>
                                            <HelpIcon sx={{ color: 'secondary.main' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                {excelReportLoading ? (
                                    <MainCard content={true} boxShadow={true} border={false}
                                        sx={{ height: "80%", bgcolor: getCardColor(lightMode) }}>
                                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                            <CircularProgress size={32} sx={{ mr: 2 }} />
                                            <Typography variant="h6">
                                                Loading Excel Report Settings
                                            </Typography>
                                        </Box>
                                    </MainCard>
                                ) : excelReportError ? (
                                    <MainCard content={true} boxShadow={true} border={false}
                                        sx={{ height: "80%", bgcolor: getCardColor(lightMode) }}>
                                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                            <ErrorOutlineIcon color="error" style={{ fontSize: 32, marginRight: 8 }} />
                                            <Typography variant="h6" color="error">
                                                Error Loading Excel Report Settings
                                            </Typography>
                                        </Box>
                                    </MainCard>
                                ) : (
                                    <MainCard content={true} boxShadow={true} border={false}
                                        sx={{ height: "80%", bgcolor: getCardColor(lightMode) }}>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <Tooltip title="Set up email recipients for Excel reports">
                                                    <IconButton>
                                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Typography variant="h5">
                                                    Generate Excel Report
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Typography variant="body1" sx={{ width: 100, ml: 1 }}>
                                                        Email(s)
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 2, flex: 1, ml: -0.5 }}>
                                                        <TextField
                                                            type="email"
                                                            value={currentEmail}
                                                            onChange={(e) => setCurrentEmail(e.target.value)}
                                                            placeholder="Enter email address"
                                                            fullWidth
                                                            onKeyPress={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    handleAddEmail();
                                                                }
                                                            }}
                                                        />
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={handleAddEmail}
                                                            sx={{
                                                                minWidth: '80px',
                                                                bgcolor: '#4cceac',
                                                                '&:hover': {
                                                                    bgcolor: '#3da88a'
                                                                }
                                                            }}
                                                        >
                                                            ADD
                                                        </Button>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ ml: 14, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {emails.map((email, index) => (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                borderRadius: theme.borderRadius.sm,
                                                                borderColor: "primary",
                                                                border: 1,
                                                                p: 1,
                                                            }}
                                                        >
                                                            <Typography sx={{ color: '#000' }}>{email}</Typography>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRemoveEmail(email)}
                                                                sx={{
                                                                    ml: 'auto',
                                                                    color: '#000',
                                                                    '&:hover': {
                                                                        color: '#ff0000'
                                                                    }
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    ))}
                                                </Box>

                                                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleSaveExcelSettings}
                                                        disabled={isSavingExcelSettings}
                                                        sx={{ px: 3, mr: 2 }}
                                                    >
                                                        {isSavingExcelSettings ? (
                                                            <CircularProgress size={24} color="inherit" />
                                                        ) : (
                                                            "SAVE SETTINGS"
                                                        )}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleGenerateReport}
                                                        disabled={isGeneratingReport || emails.length === 0}
                                                        sx={{
                                                            minWidth: '80px',
                                                            bgcolor: '#4cceac',
                                                            '&:hover': {
                                                                bgcolor: '#3da88a'
                                                            }
                                                        }}
                                                    >
                                                        {isGeneratingReport ? <CircularProgress size={24} color="inherit" /> : "SEND NOW"}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </MainCard>
                                )}
                            </Grid>
                        )}

                        {isAdmin && (
                            <Grid item xs={12} md={12} lg={12}>
                                <Typography variant="h4" sx={{ ml: 1, mt: 2, mb: 2 }}>Admin Actions</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} lg={4}>
                                        <MainCard content={true} boxShadow={true} border={false}
                                            sx={{
                                                height: "100%",
                                                bgcolor: getCardColor(lightMode)
                                            }}>
                                            <Box sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                width: "100%",
                                                justifyContent: "space-between"
                                            }}>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <Tooltip title="Sets ReportingLevel to 'Line Manager' for all line managers who currently have it as null">
                                                        <IconButton>
                                                            <HelpIcon sx={{ color: "secondary.main" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Typography variant="h5">
                                                        Apply Line Manager Reporting Level
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={handleSetLineManagerReportingLevel}
                                                    disabled={isSettingReportingLevel}
                                                >
                                                    {isSettingReportingLevel ? <CircularProgress size={24} color="inherit" /> : "APPLY"}
                                                </Button>
                                            </Box>
                                        </MainCard>
                                    </Grid>

                                    <Grid item xs={12} sm={12} md={6} lg={4}>
                                        <MainCard content={true} boxShadow={true} border={false}
                                            sx={{
                                                height: "100%",
                                                bgcolor: getCardColor(lightMode)
                                            }}>
                                            <Box sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                width: "100%",
                                                justifyContent: "space-between"
                                            }}>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <Tooltip title="Sets OnDemandLevel to 'Line Manager' for all line managers who currently have it as null">
                                                        <IconButton>
                                                            <HelpIcon sx={{ color: "secondary.main" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Typography variant="h5">
                                                        Apply Line Manager On-Demand Level
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={handleSetLineManagerOnDemandLevel}
                                                    disabled={isSettingOnDemandLevel}
                                                >
                                                    {isSettingOnDemandLevel ? <CircularProgress size={24} color="inherit" /> : "APPLY"}
                                                </Button>
                                            </Box>
                                        </MainCard>
                                    </Grid>

                                    <Grid item xs={12} sm={12} md={6} lg={4}>
                                        <MainCard content={true} boxShadow={true} border={false}
                                            sx={{
                                                height: "100%",
                                                bgcolor: getCardColor(lightMode)
                                            }}>
                                            <Box sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                width: "100%",
                                                justifyContent: "space-between"
                                            }}>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <Tooltip title="Deleting the active journey will permanently remove it and all associated data. This action is irreversible. Ensure you have saved any necessary data before proceeding">
                                                        <IconButton>
                                                            <HelpIcon sx={{ color: "secondary.main" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Typography variant="h5">
                                                        Delete active Journey
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    onClick={handleDeleteJourneysSchedules}
                                                    disabled={isDeletingJourney}
                                                >
                                                    {isDeletingJourney ? <CircularProgress size={24} color="inherit" /> : "DELETE"}
                                                </Button>
                                            </Box>
                                        </MainCard>
                                    </Grid>

                                    <Grid item xs={12} sm={12} md={6} lg={4}>
                                        <MainCard content={true} boxShadow={true} border={false}
                                            sx={{ height: "100%", bgcolor: getCardColor(lightMode) }}>
                                            <Box sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                width: "100%",
                                                justifyContent: "space-between"
                                            }}>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <Tooltip title="Deleting all tenancy users will permenantly remove all users and their associated data. This action is irreversible. Ensure you have saved any necessary data before proceeding">
                                                        <IconButton>
                                                            <HelpIcon sx={{ color: "secondary.main" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Typography variant="h5">
                                                        Delete all tenancy users
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    onClick={handleDeleteUsers}
                                                    disabled={isDeletingUsers}
                                                >
                                                    {isDeletingUsers ? <CircularProgress size={24} color="inherit" /> : "DELETE"}
                                                </Button>
                                            </Box>
                                        </MainCard>
                                    </Grid>

                                    <Grid item xs={12} sm={12} md={6} lg={4}>
                                        <MainCard content={true} boxShadow={true} border={false}
                                            sx={{
                                                height: "100%",
                                                bgcolor: getCardColor(lightMode)
                                            }}>
                                            <Box sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                width: "100%",
                                                justifyContent: "space-between"
                                            }}>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <Tooltip title="Deleting this tenancy will permanently remove it and all associated data. This action is irreversible. Ensure you have saved any necessary data before proceeding">
                                                        <IconButton>
                                                            <HelpIcon sx={{ color: "secondary.main" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Typography variant="h5">
                                                        Delete tenancy
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    onClick={handleDeleteTenancy}
                                                    disabled={isDeletingTenancy}
                                                >
                                                    {isDeletingTenancy ? <CircularProgress size={24} color="inherit" /> : "DELETE"}
                                                </Button>
                                            </Box>
                                        </MainCard>
                                    </Grid>

                                    {/*  ---------------- REMOVED PARTNER BUTTON (will be added in a later release) ----------------
                                    <Grid item xs={12} sm={12} md={6} lg={4}>
                                        <MainCard content={true} boxShadow={true} border={false}
                                            sx={{
                                                height: "100%",
                                                bgcolor: getCardColor(lightMode, tenancyData.isPartnerTenancy)
                                            }}>
                                            <Box sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                width: "100%",
                                                justifyContent: "space-between"
                                            }}>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <Tooltip title="Partner tenancies are allowed to create and provision new tenancies. Setting a tenancy as a partner will automatically label them as a host tenancy">
                                                        <IconButton>
                                                            <HelpIcon sx={{ color: "secondary.main" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Typography variant="h5">
                                                        Partner Tenancy
                                                    </Typography>
                                                </Box>
                                                <LoadingSwitch
                                                    checked={tenancyData.isPartnerTenancy}
                                                    onChange={() => handleSwitchChange('isPartnerTenancy')}
                                                    loading={loadingStates.isPartnerTenancy}
                                                    disabled={isAnySwitchLoading(loadingStates)}
                                                    inputProps={{ "aria-label": "controlled" }}
                                                />
                                            </Box>
                                        </MainCard>
                                    </Grid>
                                    */ }

                                    <Grid item xs={12} sm={12} md={6} lg={4}>
                                        <MainCard content={true} boxShadow={true} border={false}
                                            sx={{
                                                height: "100%",
                                                bgcolor: getCardColor(lightMode, tenancyData.isHierarchyTenancy)
                                            }}>
                                            <Box sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                width: "100%",
                                                justifyContent: "space-between"
                                            }}>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <Tooltip title="Host tenancies are allowed to view the tenant table and switch between any tenancies they are the parent of">
                                                        <IconButton>
                                                            <HelpIcon sx={{ color: "secondary.main" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Typography variant="h5">
                                                        Host Tenancy
                                                    </Typography>
                                                </Box>
                                                <LoadingSwitch
                                                    checked={tenancyData.isHierarchyTenancy}
                                                    onChange={() => handleSwitchChange('isHierarchyTenancy')}
                                                    loading={loadingStates.isHierarchyTenancy}
                                                    disabled={isAnySwitchLoading(loadingStates)}
                                                    inputProps={{ "aria-label": "controlled" }}
                                                />
                                            </Box>
                                        </MainCard>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                        {isAdmin && (
                            <Grid item xs={12} md={12} lg={12}>
                                <Typography variant="h4" sx={{ ml: 1, mt: 2, mb: 2 }}>Licences</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6} lg={4}>
                                        <MainCard content={true} boxShadow={true} border={false}
                                            sx={{ height: "100%", bgcolor: getCardColor(lightMode) }}>
                                            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                                <Tooltip title="Set the number of user licenses for this tenancy">
                                                    <IconButton>
                                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Typography variant="h5">
                                                    User Licenses
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                {tenancyBasicData && <Typography variant="body1">
                                                    Current Usage: {tenancyBasicData.userCount}
                                                </Typography>}
                                                <TextField
                                                    type="number"
                                                    value={tenancyData.userLicenses}
                                                    onChange={(e) => setTenancyData({
                                                        ...tenancyData,
                                                        userLicenses: parseInt(e.target.value)
                                                    })}
                                                    sx={{ width: 100 }}
                                                />
                                            </Box>
                                            <Box sx={{ display: "flex", justifyContent: "end", mt: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={handleSaveUserLicenses}
                                                >
                                                    Save Licenses
                                                </Button>
                                            </Box>
                                        </MainCard>
                                    </Grid>

                                </Grid>

                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>

            {/* Snackbar for notifications */}
            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled"
                    sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <ConfirmationDialog
                open={dialogOpen}
                title={dialogTitle}
                content={dialogContent}
                onConfirm={() => {
                    setDialogOpen(false);
                    dialogAction();
                }}
                onCancel={() => {
                    dialogCancelAction?.();
                    setDialogOpen(false);
                }}
            />
        </>
    );
}